import { Spacing } from '@persona-id/design-tokens';
import { kebabCase } from 'lodash/fp';
import { ChangeEvent, FC } from 'react';
import styled from 'styled-components';

import { TimeIntervalInputUnit } from 'dashboard/components-lib/DurationInput/constants';
import { convertTimeToDesiredUnit } from 'dashboard/components-lib/DurationInput/utils';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import { Label, Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import useToggle from 'lib/hooks/useToggle';

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const SubtextParagraph = styled(Paragraph).attrs(() => ({ size: 'sm' }))`
  margin-bottom: ${Spacing[2]};
`;

const InputContainer = styled.div`
  display: grid;
  grid-template-columns: 96px 116px;
  grid-gap: ${Spacing[2]} ${Spacing[4]};
`;

export const TIME_INTERVAL_OPTIONS = [
  { label: 'day(s)', value: TimeIntervalInputUnit.DAYS },
  { label: 'week(s)', value: TimeIntervalInputUnit.WEEKS },
  { label: 'month(s)', value: TimeIntervalInputUnit.MONTHS },
  { label: 'year(s)', value: TimeIntervalInputUnit.YEARS },
];

interface DurationInputProps {
  title: string;
  subtitle: string;
  hasError: boolean;
  onChange: (newValue: {
    rawDuration: number | undefined;
    convertedDuration: number | undefined;
    unit: TimeIntervalInputUnit;
  }) => void;
  value: { duration: number | undefined; unit: TimeIntervalInputUnit };
  desiredTimeUnit: 'days' | 'seconds';
  onToggle?: (toggleValue: boolean) => void;
  enableToggleByDefault?: boolean;
}

const DurationInput: FC<DurationInputProps> = ({
  title,
  subtitle,
  hasError,
  onChange,
  value,
  desiredTimeUnit,
  onToggle,
  enableToggleByDefault,
}) => {
  const [toggleValue, { enable: enableToggle, disable: disableToggle }] = useToggle(
    enableToggleByDefault ?? false,
  );

  return (
    <>
      <TitleContainer>
        <Label size='md' weight='bold'>
          {title}
        </Label>
        {onToggle && (
          <ToggleSlider
            value={toggleValue}
            onChange={(visible: boolean) => {
              if (visible) {
                enableToggle();
                onToggle(true);
              } else {
                disableToggle();
                onToggle(false);
              }
            }}
          />
        )}
      </TitleContainer>
      <SubtextParagraph>{subtitle}</SubtextParagraph>

      {(!onToggle || toggleValue) && (
        <InputContainer>
          <Input
            id={`duration-input__${kebabCase(title)}-input`}
            value={value.duration}
            aria-label={`${title} input`}
            hasError={hasError}
            type='number'
            placeholder='Number'
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const newInput = isNaN(event.currentTarget.valueAsNumber)
                ? undefined
                : event.currentTarget.valueAsNumber;
              onChange({
                rawDuration: newInput,
                convertedDuration: convertTimeToDesiredUnit(newInput, value.unit, desiredTimeUnit),
                unit: value.unit,
              });
            }}
          />
          <Select
            id={`duration-input__${kebabCase(title)}-time-unit`}
            value={value.unit}
            aria-label={`${title} time unit`}
            hasError={hasError}
            onValueChange={(newUnit: TimeIntervalInputUnit) => {
              onChange({
                rawDuration: value.duration,
                convertedDuration: convertTimeToDesiredUnit(
                  value.duration,
                  newUnit,
                  desiredTimeUnit,
                ),
                unit: newUnit,
              });
            }}
          >
            {TIME_INTERVAL_OPTIONS.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </InputContainer>
      )}
    </>
  );
};

export default DurationInput;
